<template>
  <div class="file-upload-component">
    <div class="upload-header">
      <el-upload
        class="upload-demo file-upload-with-scroll"
        action=""
        :http-request="handleFileUpload"
        :on-remove="handleRemove"
        :before-remove="beforeRemove"
        :before-upload="beforeFileUpload"
        :on-exceed="handleExceed"
        :on-change="handleFileChange"
        multiple
        :limit="limit"
        :file-list="reversedFileList"
        :accept="accept"
        :disabled="uploadLoading"
        :show-file-list="false">
        <el-button size="small" type="primary" :loading="uploadLoading">选择文件</el-button>
      </el-upload>
      <div v-if="totalCount > 0 && fileList && fileList.length > 0" class="upload-progress-container">
        <div class="upload-progress-info">
          <span class="upload-progress-text">{{ uploadedCount }}/{{ totalCount }}</span>
          <span v-if="isUploadComplete" class="upload-complete-text">已完成</span>
        </div>
        <el-progress 
          :percentage="uploadProgress" 
          :stroke-width="8"
          :show-text="false"
          class="upload-progress-bar">
        </el-progress>
      </div>
    </div>
    <el-upload
      class="upload-demo file-upload-with-scroll"
      action=""
      :http-request="handleFileUpload"
      :on-remove="handleRemove"
      :before-remove="beforeRemove"
      :before-upload="beforeFileUpload"
      :on-exceed="handleExceed"
      :on-change="handleFileChange"
      multiple
      :limit="limit"
      :file-list="reversedFileList"
      :accept="accept"
      :disabled="uploadLoading"
      :with-credentials="false">
      <template slot="file" slot-scope="{ file }">
        <div class="custom-file-item" :key="file.uid">
          <span class="file-name">{{ file.name }}</span>
          <span class="file-status" :class="'file-status-' + file.status">
            {{ file.status === 'success' ? '上传成功' : (file.status === 'fail' ? '上传失败' : (file.status === 'uploading' ? '上传中...' : '等待上传')) }}
          </span>
          <i 
            v-if="file.status === 'success'" 
            class="el-icon-close remove-file-btn" 
            @click="handleManualRemove(file)"
            title="移除">
          </i>
        </div>
      </template>
      <div slot="tip" class="upload-tips">{{ finalTip }}</div>
    </el-upload>
  </div>
</template>

<script>
import { toolsts } from "@/api";
import ObsClient from "esdk-obs-browserjs";

export default {
  name: 'FileUpload',
  props: {
    // 文件列表
    fileList: {
      type: Array,
      default: () => []
    },
    // 上传文件数量限制
    limit: {
      type: Number,
      default: 1000
    },
    // 接受的文件类型
    accept: {
      type: String,
      default: '.npy'
    },
    // 单个文件大小限制（KB）
    maxSize: {
      type: Number,
      default: 100
    },
    // 是否显示加载状态
    uploadLoading: {
      type: Boolean,
      default: false
    },
    // 自定义上传提示
    uploadTip: {
      type: String,
      default: ''
    },
    // 当前选中的项目数据
    selectedItem: {
      type: Object,
      default: () => null
    },
    // 当前激活的标签数据
    currentTabItem: {
      type: Object,
      default: () => null
    }
  },
  
  data() {
    return {
      // 内部加载状态
      internalLoading: false,
      // OBS配置缓存
      obsConfigCache: null,
      // OBS配置缓存时间戳
      obsConfigCacheTime: 0,
      // 缓存有效期（5分钟）
      cacheExpiry: 5 * 60 * 1000,
      // OBS配置请求锁，防止并发请求
      obsConfigPromise: null,
      // 已上传文件数量
      uploadedCount: 0,
      // 将要上传的总数量
      totalCount: 0,
      // 当前批次开始时的总数量（上传过程中保持固定）
      batchTotalCount: 0,
      // 是否处于上传批次中（从选择文件到上传完成）
      isInBatch: false
    }
  },
  
  computed: {
    // 倒序的文件列表
    reversedFileList() {
      if (!this.fileList || this.fileList.length === 0) return []
      return [...this.fileList]
    },
    // 实际的加载状态
    isLoading() {
      return this.uploadLoading || this.internalLoading
    },
    // 当前已选文件数量
    selectedFileCount() {
      return this.fileList ? this.fileList.length : 0
    },
    // 上传进度百分比
    uploadProgress() {
      if (this.totalCount === 0) return 0
      return Math.round((this.uploadedCount / this.totalCount) * 100)
    },
    // 是否上传完成
    isUploadComplete() {
      return this.totalCount > 0 && this.uploadedCount === this.totalCount && !this.isLoading
    },
    // 默认提示文本
    defaultTip() {
      if (this.accept === '.npy') {
        return `仅支持上传 .npy 格式的波形数据文件，单次最多${this.limit}个文件，单个文件最大${this.maxSize}KB`
      }
      return `仅支持上传 ${this.accept} 格式的文件，单次最多${this.limit}个文件，单个文件最大${this.maxSize}KB`
    },
    // 最终使用的提示文本
    finalTip() {
      return this.uploadTip || this.defaultTip
    }
  },
  
  watch: {
    // 监听文件列表变化，更新上传进度
    fileList: {
      handler(newVal) {
        if (newVal) {
          // 检查是否有文件正在上传
          const hasUploadingFile = newVal.some(f => 
            f.status === 'uploading' || 
            f.status === 'ready'
          )
          
          if (this.isInBatch) {
            // 在批次中，总数保持为批次总数
            this.totalCount = this.batchTotalCount
          } else {
            // 不在批次中，总数为当前文件列表的长度
            this.totalCount = newVal.length
          }
          
          // 已上传数量为状态为 success 的文件数量
          this.uploadedCount = newVal.filter(f => f.status === 'success').length
          
          console.log('fileList变化:', {
            totalCount: this.totalCount,
            uploadedCount: this.uploadedCount,
            batchTotalCount: this.batchTotalCount,
            isInBatch: this.isInBatch,
            hasUploadingFile,
            fileListStatus: newVal.map(f => ({ name: f.name, status: f.status }))
          })
        }
      },
      deep: true,
      immediate: true
    }
  },
  
  methods: {
    // 获取OBS配置（带缓存和请求锁机制）
    async getOBSConfig() {
      const now = Date.now()
      
      // 检查缓存是否有效
      if (this.obsConfigCache && (now - this.obsConfigCacheTime) < this.cacheExpiry) {
        console.log('使用缓存的OBS配置')
        return this.obsConfigCache
      }
      
      // 如果已经有正在进行的请求，直接返回该请求的Promise
      if (this.obsConfigPromise) {
        console.log('等待正在进行的OBS配置请求')
        return this.obsConfigPromise
      }
      
      console.log('发起新的OBS配置请求')
      // 创建新的请求Promise
      this.obsConfigPromise = this.fetchOBSConfig()
      
      try {
        const result = await this.obsConfigPromise
        console.log('OBS配置请求完成')
        return result
      } finally {
        // 清理请求锁
        this.obsConfigPromise = null
      }
    },
    
    // 真正获取OBS配置的方法
    async fetchOBSConfig() {
      try {
        // 获取华为云OBS配置
        const res = await toolsts()
        if (res.code !== 200) {
          throw new Error('获取上传配置失败')
        }
        
        const now = Date.now()
        // 缓存配置
        this.obsConfigCache = res.data
        this.obsConfigCacheTime = now
        
        return this.obsConfigCache
      } catch (error) {
        this.obsConfigCache = null
        this.obsConfigCacheTime = 0
        throw error
      }
    },
    
    // 清除OBS配置缓存
    clearOBSConfigCache() {
      this.obsConfigCache = null
      this.obsConfigCacheTime = 0
      this.obsConfigPromise = null
    },
    
    
    
    // 文件上传前校验
    async beforeFileUpload(file) {
      // 不区分大小写检查文件扩展名
      const fileExtension = file.name.split('.').pop().toLowerCase()
      const acceptExtension = this.accept.replace('.', '').toLowerCase()
      
      if (fileExtension !== acceptExtension) {
        this.$message.warning(`只能上传 ${this.accept} 格式的文件`)
        return false
      }
      
      // 检查文件大小
      const isValidSize = file.size / 1024  < this.maxSize
      if (!isValidSize) {
        this.$message.error(`文件大小不能超过 ${this.maxSize}KB`)
        return false
      }
      
      return true
    },
    
    // 文件超出数量限制处理
    handleExceed(files, fileList) {
      this.$message.warning(`最多只能上传${this.limit}个文件`)
    },
    // 文件选择变化处理
    handleFileChange(file, fileList) {
      // 如果是新选择的文件（状态为 ready），记录批次总数并开始批次
      if (file.status === 'ready') {
        this.batchTotalCount = fileList.length
        this.isInBatch = true
      }
      // 更新文件列表，让 watcher 自动处理进度
      this.$emit('update:fileList', fileList)
    },
    
    // 文件移除处理
    beforeRemove(file) {
      // 轻提示文件已移除，无需确认对话框
      this.$message.info(`文件 ${file.name} 已移除`)
      return true
    },
    
    // 文件移除
    handleRemove(file, fileList) {
      // watcher 会自动更新进度，这里不需要手动处理
      this.$emit('remove', file, fileList)
    },
    
    // 清空文件列表
    clearFiles() {
      this.uploadedCount = 0
      this.totalCount = 0
      this.batchTotalCount = 0
      this.isInBatch = false
      this.$emit('update:fileList', [])
    },
    
    // 结束批次（在点击确定按钮后调用）
    endBatch() {
      this.isInBatch = false
    },
    
    // 更新文件列表中的文件状态
    updateFileStatus(fileName, status, result = null) {
      if (!this.fileList || this.fileList.length === 0) return
      
      const fileIndex = this.fileList.findIndex(f => f.name === fileName)
      
      if (fileIndex !== -1) {
        this.$set(this.fileList[fileIndex], 'status', status)
        if (result) {
          this.$set(this.fileList[fileIndex], 'url', result.url)
          this.$set(this.fileList[fileIndex], 'filePath', result.filePath)
          this.$set(this.fileList[fileIndex], 'originalName', result.originalName)
          this.$set(this.fileList[fileIndex], 'uploadName', result.uploadName)
          this.$set(this.fileList[fileIndex], 'size', result.size)
        }
        this.$forceUpdate()
      }
    },
    
    // 更新文件列表中的文件状态
    updateFileStatus(fileName, status, result = null) {
      if (!this.fileList || this.fileList.length === 0) return
      
      const fileIndex = this.fileList.findIndex(f => f.name === fileName)
      
      if (fileIndex !== -1) {
        this.$set(this.fileList[fileIndex], 'status', status)
        if (result) {
          this.$set(this.fileList[fileIndex], 'url', result.url)
          this.$set(this.fileList[fileIndex], 'filePath', result.filePath)
          this.$set(this.fileList[fileIndex], 'originalName', result.originalName)
          this.$set(this.fileList[fileIndex], 'uploadName', result.uploadName)
          this.$set(this.fileList[fileIndex], 'size', result.size)
        }
        this.$forceUpdate()
      }
    },
    
    // 从文件列表中移除指定文件
    removeFileFromList(fileName) {
      if (!this.fileList || this.fileList.length === 0) return
      
      const fileIndex = this.fileList.findIndex(f => f.name === fileName)
      if (fileIndex !== -1) {
        this.fileList.splice(fileIndex, 1)
        this.$emit('update:fileList', this.fileList)
      }
    },
    
    // 手动移除已上传成功的文件
    handleManualRemove(file) {
      this.removeFileFromList(file.name)
      this.$emit('manual-remove', file)
    },
    
    // 自定义文件上传（华为云OBS）
    async handleFileUpload(option) {
      try {
        this.internalLoading = true
        this.$emit('loading', true)
        
        const file = option.file
        const pos = file.name.lastIndexOf(".")
        let originalFilename = file.name.substring(0, pos)
        const extendName = file.name.substring(pos + 1)
        
        // 进一步处理originalFilename，去掉其中可能包含的后缀名（如.npy）
        originalFilename = originalFilename.replace(/\.[^.]*$/, '')
        
        // 获取当前日期
        const now = new Date()
        const year = now.getFullYear()
        const month = String(now.getMonth() + 1).padStart(2, '0')
        const day = String(now.getDate()).padStart(2, '0')
        const dateStr = `${year}${month}${day}`
        
        // 生成13位随机数字
        const randomNum = Math.floor(Math.random() * 10000000000000).toString().padStart(13, '0')
        
        // 构建最终文件名：基础文件名_年月日_随机数.npy
        const finalFilename = `${originalFilename}_${dateStr}_${randomNum}.npy`
        
        // 获取当前选中的项目ID和当前标签页的itemCode
        const selectedItemId = this.selectedItem ? this.selectedItem.id : ''
        const currentItemCode = this.currentTabItem ? this.currentTabItem.itemCode : ''
        
        if (!selectedItemId) {
          this.$message.error('未获取到选中的项目ID，无法上传文件')
          option.onError(new Error('未获取到选中的项目ID'))
          return
        }
        
        const fileNames = `lxwlw/waveNpy/${currentItemCode}/${selectedItemId}/${originalFilename}.npy`
        
        // 获取华为云OBS配置（使用缓存机制）
        let obsConfig
        try {
          obsConfig = await this.getOBSConfig()
        } catch (error) {
          this.$message.error('获取上传配置失败')
          option.onError(new Error('获取上传配置失败'))
          return
        }
        
        const { access, secret, bucket, endpoint, securityToken } = obsConfig
        const obsClient = new ObsClient({
          access_key_id: access,
          secret_access_key: secret,
          server: `https://${endpoint}`,
          security_token: securityToken,
          x_obs_acl: "public-read",
        })
        
        // 上传文件到华为云OBS
        const uploadResult = await obsClient.putObject({
          Bucket: bucket,
          Key: fileNames,
          SourceFile: file,
        })
        
        if (uploadResult.CommonMsg.Status < 300) {
          // this.$message.success(`${file.name} 上传成功`)
          
          // 构建返回数据
          const result = {
            name: file.name,
            url: `https://${bucket}.${endpoint}/${fileNames}`,
            filePath: fileNames,
            originalName: file.name,
            uploadName: finalFilename,
            size: file.size
          }
          
          option.onSuccess(result)
          this.$emit('success', result)
          
          this.updateFileStatus(file.name, 'success', result)
        } else {
          this.$message.error(`${file.name} 上传失败`)
          option.onError(new Error('上传失败'))
          this.$emit('error', new Error('上传失败'), file)
          
          // 手动更新文件列表中的文件状态
          this.updateFileStatus(file.name, 'fail')
        }
      } catch (error) {
        console.error('文件上传失败:', error)
        this.$message.error('文件上传失败')
        option.onError(error)
        this.$emit('error', error, file)
        
        // 手动更新文件列表中的文件状态
        this.updateFileStatus(file.name, 'fail')
      } finally {
        this.internalLoading = false
        this.$emit('loading', false)
      }
    }
  }
}
</script>

<style scoped>
.file-upload-component {
  width: 100%;
}

/* 上传头部容器 */
.upload-header {
  display: flex;
  align-items: center;
  /* margin-bottom: 10px; */
}

/* 上传进度容器 */
.upload-progress-container {
  display: inline-flex;
  align-items: center;
  margin-left: 15px;
  padding: 6px 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
  vertical-align: middle;
}

/* 上传进度信息 */
.upload-progress-info {
  margin-right: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 上传进度文本 */
.upload-progress-text {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  white-space: nowrap;
  max-width: 70px;
  text-align: center;
}

/* 上传完成文本 */
.upload-complete-text {
  font-size: 14px;
  color: #67C23A;
  font-weight: 600;
  white-space: nowrap;
}

/* 上传进度条 */
.upload-progress-bar {
  width: 150px;
  display: inline-block;
}

.upload-progress-bar ::v-deep .el-progress-bar__outer {
  background-color: #e4e7ed;
}

.upload-progress-bar ::v-deep .el-progress-bar__inner {
  background: linear-gradient(90deg, #00d8c3 0%, #00c4b3 100%);
  transition: width 0.3s ease;
}

/* 上传加载中时，按钮禁止点击的样式 */
.file-upload-with-scroll[disabled] .el-button {
  cursor: not-allowed !important;
}

.file-upload-with-scroll[disabled] .el-button:hover {
  opacity: 0.8;
  cursor: not-allowed !important;
}
</style>

<style>
.file-upload-with-scroll .el-upload-list {
  max-height: 300px;
  overflow-y: auto;
  padding-right: 8px;
}

/* 自定义滚动条样式 */
.file-upload-with-scroll .el-upload-list::-webkit-scrollbar {
  width: 6px;
}

.file-upload-with-scroll .el-upload-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.file-upload-with-scroll .el-upload-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.file-upload-with-scroll .el-upload-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.upload-tips {
  transform: translateY(-15px);
  padding: 8px 10px;
  background-color: #fef0f0;
  border-left: 4px solid #fde2e2;
  border-radius: 4px;
  color: #f56c6c;
  font-size: 12px;
  line-height: 1.5;
  cursor: default;
}

.custom-file-item {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  margin: 4px 0;
  border-radius: 4px;
  border: 1px solid #ebeef5;
  transition: all 0.3s ease;
}

.custom-file-item:hover {
  background-color: #f5f7fa;
  border-color: #e4e7ed;
}

.file-name {
  flex: 1;
  max-width: 350px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #606266;
  font-size: 14px;
}

.file-status {
  flex-shrink: 0;
  font-size: 12px;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 4px;
}

.file-status-success {
  color: #67c23a;
  background-color: #f0f9eb;
}

.file-status-fail {
  color: #f56c6c;
  background-color: #fef0f0;
}

.file-status-uploading {
  color: #409eff;
  background-color: #ecf5ff;
}

.file-status-ready {
  color: #909399;
  background-color: #f4f4f5;
}

.remove-file-btn {
  flex-shrink: 0;
  margin-left: 8px;
  padding: 4px;
  color: #c0c4cc;
  cursor: pointer;
  font-size: 14px;
  border-radius: 4px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-file-btn:hover {
  color: #f56c6c;
  background-color: #fef0f0;
}
</style>